/**
 * 🔴 A TRILHA — "por que o cliente X não recebeu?" (adendo de 11/09/2026).
 *
 * A auditoria achou a rota `GET /admin/whatsapp/trilha` **quebrada** (HTTP 500,
 * por uma coluna que não existe) e **sem tela nenhuma**: o sistema registrava o
 * texto exato de tudo que saiu, foi bloqueado ou falhou — num lugar que ninguém
 * conseguia abrir. Enquanto isso, 18 falhas de envio passaram despercebidas.
 *
 * ⚠️ O motivo aparece em PORTUGUÊS. "Bloqueado" sozinho não responde a pergunta
 * que esta tela existe para responder.
 */
import { useCallback, useEffect, useState } from 'react';

import { Alert, Badge, Button, HelpTip, Input, Select } from '@/components/ui';
import { api } from '@/lib/api';

interface LinhaDaTrilha {
  id: string;
  resultado: 'saiu' | 'bloqueado' | 'falhou';
  quando: string;
  quem: string | null;
  grupoId: string | null;
  grupoNome: string | null;
  clienteId: string | null;
  gatilho: string | null;
  origem: string | null;
  texto: string | null;
  motivo: string | null;
  porque: string | null;
  detalhe: string | null;
}

const SELO: Record<
  LinhaDaTrilha['resultado'],
  { texto: string; variant: 'success' | 'warning' | 'danger' }
> = {
  saiu: { texto: 'saiu', variant: 'success' },
  bloqueado: { texto: 'bloqueado', variant: 'warning' },
  falhou: { texto: 'falhou', variant: 'danger' },
};

const AJUDA =
  'Tudo que o sistema tentou mandar pelo WhatsApp fica aqui: o que saiu (com o texto exato), o ' +
  'que foi bloqueado antes de sair e o que o WhatsApp recusou. É onde se responde "por que o ' +
  'cliente não recebeu?" — cada linha diz o motivo em português. Filtre por cliente para ver a ' +
  'história de um condomínio só.';

function quando(iso: string): string {
  const s = /[zZ]|[+-]\d\d:\d\d$/.test(iso) ? iso : `${iso.replace(' ', 'T')}Z`;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return iso;
  const w = new Date(d.getTime() - 3 * 3600_000);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(w.getUTCDate())}/${p(w.getUTCMonth() + 1)} ${p(w.getUTCHours())}:${p(w.getUTCMinutes())}`;
}

export function TrilhaDoWhatsapp({
  grupos,
}: {
  /** Para o filtro por grupo — os mesmos da aba Grupos. */
  grupos: Array<{ grupoId: string; nome: string; clienteId?: string | null }>;
}) {
  const [linhas, setLinhas] = useState<LinhaDaTrilha[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(false);
  /**
   * 🔴 ABERTA (adendo 12/09). Quando a trilha morava no rodapé de outra aba,
   * nascer fechada era cortesia: ninguém abriu "Conexão e limites" para ver
   * histórico. Agora ela **é** a aba — e uma aba que abre vazia, com um botão
   * "Ver o histórico", é a mesma porta escondida um nível abaixo. Quem clicou
   * em "Trilha de mensagens" já disse o que quer.
   */
  const [aberta, setAberta] = useState(true);

  const [resultado, setResultado] = useState('');
  const [grupoId, setGrupoId] = useState('');
  const [desde, setDesde] = useState('');
  const [expandida, setExpandida] = useState<string | null>(null);

  const carregar = useCallback(() => {
    setCarregando(true);
    setErro(null);
    const q = new URLSearchParams();
    if (resultado) q.set('resultado', resultado);
    if (grupoId) q.set('grupoId', grupoId);
    if (desde) q.set('desde', desde);
    api
      .get<{ linhas: LinhaDaTrilha[] }>(
        `/api/v1/admin/whatsapp/trilha${q.toString() ? `?${q.toString()}` : ''}`,
      )
      .then((r) => setLinhas(r.linhas))
      .catch(() => setErro('Não foi possível ler o histórico.'))
      .finally(() => setCarregando(false));
  }, [resultado, grupoId, desde]);

  useEffect(() => {
    if (aberta) carregar();
  }, [aberta, carregar]);

  const contagem = (r: LinhaDaTrilha['resultado']) => (linhas ?? []).filter((l) => l.resultado === r).length;

  return (
    <div data-testid="wa-trilha">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 text-sm font-medium text-ink-1 dark:text-ink-dark-1">
          Histórico de mensagens
          <HelpTip texto={AJUDA} />
        </span>
        <Button size="sm" variant="ghost" onClick={() => setAberta(!aberta)} data-testid="wa-trilha-abrir">
          {aberta ? 'Recolher' : 'Ver o histórico'}
        </Button>
      </div>

      {aberta && (
        <div className="mt-3 space-y-3">
          {erro && <Alert variant="danger">{erro}</Alert>}

          <div className="flex flex-wrap items-end gap-2">
            <div>
              <label htmlFor="tr-resultado" className="mb-1 block text-xs text-ink-3 dark:text-ink-dark-3">
                O que aconteceu
              </label>
              <Select
                id="tr-resultado"
                value={resultado}
                onChange={(e) => setResultado(e.target.value)}
                data-testid="wa-trilha-resultado"
              >
                <option value="">Tudo</option>
                <option value="saiu">Saiu</option>
                <option value="bloqueado">Bloqueado antes de sair</option>
                <option value="falhou">O WhatsApp recusou</option>
              </Select>
            </div>
            <div className="min-w-52">
              <label htmlFor="tr-grupo" className="mb-1 block text-xs text-ink-3 dark:text-ink-dark-3">
                Grupo (ou cliente)
              </label>
              <Select
                id="tr-grupo"
                value={grupoId}
                onChange={(e) => setGrupoId(e.target.value)}
                data-testid="wa-trilha-grupo"
              >
                <option value="">Todos</option>
                {grupos.map((g) => (
                  <option key={g.grupoId} value={g.grupoId}>
                    {g.nome}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <label htmlFor="tr-desde" className="mb-1 block text-xs text-ink-3 dark:text-ink-dark-3">
                A partir de
              </label>
              <Input
                id="tr-desde"
                type="date"
                value={desde}
                onChange={(e) => setDesde(e.target.value)}
                data-testid="wa-trilha-desde"
              />
            </div>
            <Button size="sm" variant="secondary" loading={carregando} onClick={carregar}>
              Atualizar
            </Button>
          </div>

          {linhas && linhas.length > 0 && (
            <p className="text-xs text-ink-3 dark:text-ink-dark-3">
              {linhas.length} registros · {contagem('saiu')} saíram · {contagem('bloqueado')}{' '}
              bloqueados · {contagem('falhou')} recusados pelo WhatsApp
            </p>
          )}

          <div className="max-h-96 space-y-1 overflow-y-auto rounded-lg border border-line p-2 dark:border-line-dark">
            {linhas === null ? (
              <p className="p-2 text-sm text-ink-3 dark:text-ink-dark-3">Carregando…</p>
            ) : linhas.length === 0 ? (
              <p className="p-2 text-sm text-ink-3 dark:text-ink-dark-3">
                Nada registrado com esses filtros.
              </p>
            ) : (
              linhas.map((l) => (
                <div key={l.id} className="rounded px-1 py-1 text-sm" data-testid={`wa-trilha-linha-${l.id}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant={SELO[l.resultado].variant} size="sm">
                      {SELO[l.resultado].texto}
                    </Badge>
                    <span className="text-xs text-ink-3 dark:text-ink-dark-3">{quando(l.quando)}</span>
                    <span className="text-ink-1 dark:text-ink-dark-1">{l.grupoNome ?? '—'}</span>
                    {l.gatilho && (
                      <span className="text-xs text-ink-3 dark:text-ink-dark-3">· {l.gatilho}</span>
                    )}
                    {l.texto && (
                      <button
                        type="button"
                        className="text-xs text-ink-3 underline dark:text-ink-dark-3"
                        onClick={() => setExpandida(expandida === l.id ? null : l.id)}
                      >
                        {expandida === l.id ? 'esconder o texto' : 'ver o texto'}
                      </button>
                    )}
                  </div>
                  {l.porque && (
                    <p className="text-xs text-ink-2 dark:text-ink-dark-2">
                      {l.resultado === 'saiu' ? '' : `Não saiu porque ${l.porque}.`}
                      {l.detalhe && l.resultado !== 'saiu' ? ` (${l.detalhe})` : ''}
                    </p>
                  )}
                  {expandida === l.id && l.texto && (
                    <pre className="mt-1 whitespace-pre-wrap rounded bg-surface-2 p-2 text-xs text-ink-1 dark:bg-surface-dark-2 dark:text-ink-dark-1">
                      {l.texto}
                    </pre>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
